/**
 * Algolia client + small set of shared helpers used by both the search results
 * page (#41) and the predictive search overlay (#42).
 *
 * Loaded as a regular asset (non-module) so it runs in the global scope. The
 * file relies on `algoliasearch` (the lite UMD build) having been loaded
 * earlier in the page; both consumers gate that script include behind a
 * `template.name == 'search'` / overlay-mounted check in `theme.liquid`.
 *
 * Public API (all hung off `window.RTLAlgolia`):
 *   - createSearchClient({ applicationId, searchApiKey })  → algoliasearch client (adds Insights params, #341)
 *   - getConfig()                                          → { applicationId, searchApiKey, productsIndexPrefix, hitsPerPage, productsIndex, collectionsIndex, suggestionsIndex, replicas, currency, currencyCode, locale, searchUrl }
 *   - normaliseSearchParams(searchString)                  → { query: string, tab: 'products'|'collections'|null, raw: URLSearchParams }
 *   - replaceQueryParam(name, value)                       → push canonical URL state without reload
 *   - formatPrice(amount)                                  → '$24.95' (uses theme currency)
 *   - escapeHtml(str)                                      → HTML-safe string for templating
 *   - hitProductUrl(hit)                                   → /products/<handle> (locale-aware)
 *   - hitCollectionUrl(hit)                                → /collections/<handle> (locale-aware)
 *   - resolveBadges(hit)                                   → { suppressed, promo, status, statusModifier }
 *   - getRecommendations({ objectID, model, … })           → Promise<hit[]> (Algolia Recommend, adds Insights params, #341)
 *   - hitsToHandles(hits)                                  → unique product handles, rank preserved
 *
 * The module is defensive — it never throws if `window.theme.algolia` is
 * absent (the call site decides what to do); it just returns sensible
 * defaults so a misconfigured store does not crash the page.
 */
(function () {
  'use strict';

  var DEFAULT_CONFIG = {
    applicationId: '',
    searchApiKey: '',
    productsIndexPrefix: 'au',
    hitsPerPage: 24,
    enableCollectionsTab: true,
  };

  // Memoised config — computed on first read, invalidated by `resetConfigCache()`
  // (currently only the test harness needs the reset hook). Production reads
  // hit hot paths like `formatPrice` on every product hit (24 hits per page),
  // so caching saves the prefix/replica string concat on every call.
  var _configCache = null;

  function readThemeConfig() {
    if (_configCache) return _configCache;

    var fromWindow =
      window.theme && window.theme.algolia ? window.theme.algolia : {};
    var shop = window.theme && window.theme.shop ? window.theme.shop : {};
    var routes = window.theme && window.theme.routes ? window.theme.routes : {};

    var merged = Object.assign({}, DEFAULT_CONFIG, fromWindow);
    var prefix = merged.productsIndexPrefix || 'au';

    _configCache = {
      applicationId: merged.applicationId,
      searchApiKey: merged.searchApiKey,
      productsIndexPrefix: prefix,
      hitsPerPage: parseInt(merged.hitsPerPage, 10) || 24,
      enableCollectionsTab: merged.enableCollectionsTab !== false,
      productsIndex: prefix + '_products',
      collectionsIndex: prefix + '_collections',
      suggestionsIndex: prefix + '_products_query_suggestions',
      replicas: {
        featured: prefix + '_products',
        price_asc: prefix + '_products_price_asc',
        price_desc: prefix + '_products_price_desc',
        newest: prefix + '_products_newest',
        best_selling: prefix + '_products_best_selling',
      },
      currency: shop.currencySymbol || '$',
      currencyCode: shop.currencyCode || 'AUD',
      locale: shop.locale || 'en-AU',
      searchUrl: routes.search_url || '/search',
    };
    return _configCache;
  }

  function resetConfigCache() {
    _configCache = null;
  }

  /**
   * Build an algoliasearch lite client. Returns null if the UMD bundle has
   * not been loaded — both consumers should defer their init until the
   * algoliasearch global is available.
   */
  function createSearchClient(overrides) {
    var cfg = Object.assign({}, readThemeConfig(), overrides || {});
    if (
      typeof window.algoliasearch === 'undefined' &&
      typeof window.algoliasearchLite === 'undefined'
    ) {
      return null;
    }
    var factory =
      (typeof window.algoliasearch !== 'undefined' &&
        (window.algoliasearch.liteClient ||
          window.algoliasearch.searchClient ||
          window.algoliasearch)) ||
      window.algoliasearchLite;
    if (typeof factory !== 'function') return null;
    return withInsightsParams(factory(cfg.applicationId, cfg.searchApiKey));
  }

  /**
   * Merge `RTLAlgoliaInsights.searchParams(indexName)` -- `clickAnalytics` and
   * the shopper's `userToken` -- into every request the client sends (#341),
   * so the overlay and InstantSearch both get a `queryID` back without each
   * call site having to know about consent. Read per request, so a shopper
   * who accepts analytics mid-session is tracked from their next search.
   *
   * Handles both request shapes the client accepts: the v5
   * `{ requests: [{ indexName, ...params }] }` the overlay sends and the
   * legacy `[{ indexName, params }]` array InstantSearch sends.
   */
  function withInsightsParams(client) {
    if (!client || typeof client.search !== 'function') return client;
    var search = client.search;

    function decorate(request) {
      var insights = window.RTLAlgoliaInsights;
      if (!request || !insights || typeof insights.searchParams !== 'function') {
        return request;
      }
      var extra = insights.searchParams(request.indexName);
      if (!extra) return request;
      if (request.params && typeof request.params === 'object') {
        return Object.assign({}, request, {
          params: Object.assign({}, request.params, extra),
        });
      }
      return Object.assign({}, request, extra);
    }

    client.search = function (params, requestOptions) {
      var decorated = params;
      if (Array.isArray(params)) {
        decorated = params.map(decorate);
      } else if (params && Array.isArray(params.requests)) {
        decorated = Object.assign({}, params, {
          requests: params.requests.map(decorate),
        });
      }
      return search.call(client, decorated, requestOptions);
    };
    return client;
  }

  /**
   * Read both `?q=` (Shopify-native) and `?query=` (legacy headless) from a
   * URLSearchParams instance. Returns the first non-empty match. Also surfaces
   * an optional `tab` parameter so the results page can persist the active
   * tab in the URL.
   */
  function normaliseSearchParams(searchString) {
    var raw = new URLSearchParams(
      typeof searchString === 'string' ? searchString : window.location.search
    );
    var query = raw.get('q');
    if (!query) query = raw.get('query');
    if (!query) query = '';
    var tab = raw.get('tab');
    if (tab !== 'products' && tab !== 'collections') tab = null;
    return { query: query, tab: tab, raw: raw };
  }

  /**
   * Replace (or remove, when value is empty) a single URL parameter and push
   * the result into `history.replaceState` so we never trigger a reload. The
   * canonical search URL uses `?q=`; this helper deliberately strips the
   * legacy `?query=` if present after normalisation.
   */
  function replaceQueryParam(name, value) {
    var url = new URL(window.location.href);
    var params = url.searchParams;
    if (name === 'q') {
      params.delete('query');
    }
    if (value === null || value === undefined || value === '') {
      params.delete(name);
    } else {
      params.set(name, value);
    }
    url.search = params.toString();
    try {
      window.history.replaceState(window.history.state, '', url.toString());
    } catch (err) {
      /* Silently swallow — non-fatal */
    }
  }

  /**
   * Format a numeric Algolia `price` field using the shop currency.
   * Falls back to a simple `${symbol}${value}` if Intl is unavailable.
   */
  function formatPrice(amount) {
    var cfg = readThemeConfig();
    var numeric = typeof amount === 'number' ? amount : parseFloat(amount);
    if (isNaN(numeric)) return '';
    try {
      return new Intl.NumberFormat(cfg.locale, {
        style: 'currency',
        currency: cfg.currencyCode,
      }).format(numeric);
    } catch (err) {
      return cfg.currency + numeric.toFixed(2);
    }
  }

  function escapeHtml(str) {
    if (str === null || str === undefined) return '';
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  /**
   * Validate a candidate hit URL — only http(s) absolute URLs and same-origin
   * relative URLs are allowed. Reject `javascript:`, `data:`, `vbscript:`, etc.
   * Returns the original URL when safe, otherwise `'#'`. The Algolia hit
   * payload is merchant-controlled today but the predictive overlay (#42) will
   * surface query-suggestion records too, so guard at the helper layer rather
   * than at each call site.
   */
  function sanitiseHitUrl(url) {
    if (typeof url !== 'string' || !url) return '#';
    // Relative path / hash / query — always safe (same-origin).
    if (
      url.charAt(0) === '/' ||
      url.charAt(0) === '#' ||
      url.charAt(0) === '?'
    ) {
      return url;
    }
    // Absolute URL — only http(s) allowed.
    if (/^https?:\/\//i.test(url)) return url;
    return '#';
  }

  function hitProductUrl(hit) {
    if (!hit) return '#';
    if (hit.url) return sanitiseHitUrl(hit.url);
    if (hit.handle) return '/products/' + encodeURIComponent(hit.handle);
    return '#';
  }

  function hitCollectionUrl(hit) {
    if (!hit) return '#';
    if (hit.url) return sanitiseHitUrl(hit.url);
    if (hit.handle) return '/collections/' + encodeURIComponent(hit.handle);
    return '#';
  }

  /* ------------------------------------------------------------------ */
  /* Recommend                                                          */
  /* ------------------------------------------------------------------ */

  /**
   * Algolia Recommend. Mirrors the headless
   * `tools/apis/algolia/index.ts` → `getAlgoliaRecommendations()` contract:
   * POST /1/indexes/*\/recommendations against `<prefix>_products` with
   * `threshold: 0`. The headless passed the product's FIRST VARIANT id as the
   * objectID, because Algolia's Shopify integration keys records by variant —
   * callers must do the same or the model returns nothing.
   *
   * Called with raw `fetch` rather than through the algoliasearch UMD bundle
   * so a recommendations rail never has to wait on (or fail with) the search
   * bundle — the two features are independent.
   *
   * Resolves with the hit array (possibly empty). Rejects on network / HTTP
   * error so the caller can fall back to Shopify's native recommendations.
   *
   * Insights (#341): `RTLAlgoliaInsights.searchParams()` -- `clickAnalytics`
   * and the shopper's `userToken` -- goes into `queryParameters` the same way
   * `withInsightsParams` adds it to searches, so the response carries a
   * `queryID` when analytics consent allows it. Each hit is then annotated
   * the way InstantSearch annotates search hits: `__queryID`, `__position`
   * (1-based rank in the response) and `__indexName`. Without a `queryID`
   * the hits are left as they are, so untracked recommendations send nothing.
   */
  var RECOMMEND_MODELS = [
    'related-products',
    'bought-together',
    'trending-items',
    'looking-similar',
  ];

  function getRecommendations(options) {
    var opts = options || {};
    var cfg = readThemeConfig();
    var objectID = opts.objectID ? String(opts.objectID) : '';
    var model =
      RECOMMEND_MODELS.indexOf(opts.model) !== -1
        ? opts.model
        : 'related-products';
    var maxRecommendations = parseInt(opts.maxRecommendations, 10) || 12;

    // `trending-items` ranks the whole index and rejects an `objectID`
    // ("Unknown field objectID"), so it is the one model that takes no seed.
    const isTrending = model === 'trending-items';
    if (!objectID && !isTrending) {
      return Promise.reject(new Error('getRecommendations: objectID required'));
    }
    if (!cfg.applicationId || !cfg.searchApiKey) {
      return Promise.reject(
        new Error('getRecommendations: Algolia not configured')
      );
    }

    var request = {
      indexName: opts.indexName || cfg.productsIndex,
      model: model,
      threshold: typeof opts.threshold === 'number' ? opts.threshold : 0,
      maxRecommendations: maxRecommendations,
    };
    if (!isTrending) request.objectID = objectID;
    // `bought-together` rejects queryParameters outright, so it gets neither
    // the facet filter nor the Insights params (and stays untracked); the
    // others accept both, and callers can enforce the PDP gender gate
    // server-side.
    if (model !== 'bought-together') {
      const queryParameters = {};
      if (opts.facetFilters) queryParameters.facetFilters = opts.facetFilters;
      const insights = window.RTLAlgoliaInsights;
      const tracking =
        insights && typeof insights.searchParams === 'function'
          ? insights.searchParams(request.indexName)
          : null;
      if (tracking) Object.assign(queryParameters, tracking);
      if (Object.keys(queryParameters).length) {
        request.queryParameters = queryParameters;
      }
    }

    return fetch(
      'https://' +
        cfg.applicationId +
        '-dsn.algolia.net/1/indexes/*/recommendations',
      {
        method: 'POST',
        headers: {
          'X-Algolia-API-Key': cfg.searchApiKey,
          'X-Algolia-Application-Id': cfg.applicationId,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ requests: [request] }),
      }
    )
      .then(function (response) {
        if (!response.ok) {
          throw new Error('Algolia Recommend failed: ' + response.status);
        }
        return response.json();
      })
      .then(function (payload) {
        var results = payload && payload.results;
        if (!results || !results.length) return [];
        const hits = results[0].hits || [];
        const queryID = results[0].queryID;
        if (queryID) {
          hits.forEach(function (hit, i) {
            if (!hit) return;
            hit.__queryID = queryID;
            hit.__position = i + 1;
            hit.__indexName = request.indexName;
          });
        }
        return hits;
      });
  }

  /**
   * Reduce Recommend hits to unique product handles, preserving Algolia's
   * ranked order. Variant-keyed indices return one record per variant, so the
   * same product can appear several times — dedupe or the rail repeats cards.
   */
  function hitsToHandles(hits) {
    var handles = [];
    var seen = {};
    (hits || []).forEach(function (hit) {
      var handle = hit && hit.handle ? String(hit.handle) : '';
      if (!handle && hit && typeof hit.url === 'string') {
        var match = hit.url.match(/\/products\/([^/?#]+)/);
        if (match) handle = decodeURIComponent(match[1]);
      }
      if (!handle || seen[handle]) return;
      seen[handle] = true;
      handles.push(handle);
    });
    return handles;
  }

  /* ------------------------------------------------------------------ */
  /* Badges (#327)                                                      */
  /* ------------------------------------------------------------------ */

  var DAY_SECONDS = 86400;
  var THIRTY_DAYS_SECONDS = 2592000;
  var NINETY_DAYS_SECONDS = 7776000;

  var DEFAULT_BADGE_LABELS = {
    sale: 'Sale',
    new: 'New',
    back_in_stock: 'Back in stock',
    restocking: 'Restocking',
  };

  function readBadgeConfig() {
    var fromWindow =
      window.theme && window.theme.badges ? window.theme.badges : {};
    var labels = fromWindow.labels || {};
    return {
      newBadgeDays: parseInt(fromWindow.newBadgeDays, 10) || 30,
      labels: Object.assign({}, DEFAULT_BADGE_LABELS, labels),
    };
  }

  /** Downcase + collapse `-` to ` ` + trim — the JS twin of Liquid's
   *  `| downcase | replace: '-', ' '` normalisation. */
  function normaliseTag(value) {
    if (value === null || value === undefined) return '';
    return String(value).toLowerCase().split('-').join(' ').trim();
  }

  /** Seconds-since-epoch for an indexed ISO-8601 timestamp; NaN when absent. */
  function toEpochSeconds(value) {
    if (!value) return NaN;
    var ms = Date.parse(value);
    if (isNaN(ms)) return NaN;
    return Math.floor(ms / 1000);
  }

  function toNumber(value) {
    if (value === null || value === undefined || value === '') return NaN;
    return typeof value === 'number' ? value : parseFloat(value);
  }

  /**
   * Collect every manual `badge:<text>` value on a hit.
   *
   * The Algolia Shopify integration parses `key:value` tags OUT of `tags` and
   * into `named_tags` — a `badge: BEST SELLER` tag arrives as
   * `named_tags.badge === ' BEST SELLER'` (leading space preserved), or as an
   * ARRAY when a product carries more than one. Reading `hit.tags` for a
   * `badge:` prefix — which the old search card did — can therefore never see
   * a promo badge. `tags` is still swept as a defensive fallback in case the
   * index configuration changes.
   */
  function collectBadgeValues(hit) {
    var values = [];
    var named = hit && hit.named_tags ? hit.named_tags.badge : null;
    if (named !== null && named !== undefined) {
      if (Object.prototype.toString.call(named) === '[object Array]') {
        values = values.concat(named);
      } else {
        values.push(named);
      }
    }
    var tags = hit && hit.tags;
    if (tags && typeof tags.length === 'number') {
      for (var i = 0; i < tags.length; i += 1) {
        var tag = String(tags[i]);
        if (tag.slice(0, 6).toLowerCase() === 'badge:') {
          values.push(tag.slice(6));
        }
      }
    }
    return values;
  }

  /**
   * Resolve the badges for an Algolia product hit.
   *
   * A 1:1 port of `snippets/product-badge.liquid` in its product-card mode
   * (`show_both` omitted), so a product shows the same badge on `/search` as
   * it does on the PLP:
   *
   *   1. Manual promo — first usable `badge:<text>` value. `badge:none`
   *      suppresses ALL badges; a `badge: back in stock` value is ignored.
   *   2. Status — ONE of RESTOCKING > BACK IN STOCK > NEW > SALE.
   *   3. On a card the promo OVERRIDES the status badge; the status badge is
   *      the fallback for an untagged product.
   *
   * NEW requires the product to be BOTH published within `newBadgeDays` AND
   * created within the last 90 days, so a republished restock does not read as
   * new (#317).
   *
   * RESTOCKING keys on the `custom.back_in_stock_date` metafield, which is NOT
   * currently in the Algolia index (`meta` is `{}` on every record) — the
   * branch is implemented against `hit.meta.custom.back_in_stock_date` so that
   * adding the metafield to the Algolia app's indexed metafields lights it up
   * with no code change. Until then a hit resolves BACK IN STOCK from the tag
   * alone and never RESTOCKING.
   *
   * @param {Object} hit - An Algolia product record.
   * @returns {{suppressed: boolean, promo: string, status: string, statusModifier: string}}
   */
  function resolveBadges(hit) {
    var result = {
      suppressed: false,
      promo: '',
      status: '',
      statusModifier: '',
    };
    if (!hit) return result;

    var config = readBadgeConfig();

    // --- Manual `badge:<text>` promo + `badge:none` suppress ---
    var values = collectBadgeValues(hit);
    for (var i = 0; i < values.length; i += 1) {
      var raw = String(values[i]).trim();
      var norm = normaliseTag(raw);
      if (raw !== '' && norm !== 'none' && norm !== 'back in stock') {
        if (result.promo === '') result.promo = raw;
      } else if (norm === 'none') {
        result.suppressed = true;
      }
    }
    if (result.suppressed) {
      return { suppressed: true, promo: '', status: '', statusModifier: '' };
    }

    var now = Math.floor(Date.now() / 1000);

    // --- BACK IN STOCK (plain tag) ---
    var isBackInStock = false;
    var tags = hit.tags;
    if (tags && typeof tags.length === 'number') {
      for (var t = 0; t < tags.length; t += 1) {
        if (normaliseTag(tags[t]) === 'back in stock') isBackInStock = true;
      }
    }

    // --- RESTOCKING / BACK IN STOCK (custom.back_in_stock_date metafield) ---
    // Metafield takes priority over the tag. UPCOMING date inside the next 30d
    // -> RESTOCKING (#204); past date within the last 30d -> BACK IN STOCK.
    var meta = hit.meta && hit.meta.custom ? hit.meta.custom : null;
    var bisRaw = meta ? meta.back_in_stock_date : null;
    var isRestocking = false;
    if (bisRaw) {
      var bisSeconds = toEpochSeconds(bisRaw);
      if (!isNaN(bisSeconds)) {
        if (bisSeconds >= now) {
          if (bisSeconds <= now + THIRTY_DAYS_SECONDS) isRestocking = true;
          isBackInStock = false;
        } else if (bisSeconds >= now - THIRTY_DAYS_SECONDS) {
          isBackInStock = true;
        } else {
          isBackInStock = false;
        }
      }
    }

    // --- NEW: published within the window AND created within 90 days ---
    var published = toEpochSeconds(hit.published_at);
    var created = toEpochSeconds(hit.created_at);
    var newCutoff = now - config.newBadgeDays * DAY_SECONDS;
    var maxAgeCutoff = now - NINETY_DAYS_SECONDS;
    var isNew =
      !isNaN(published) &&
      published >= newCutoff &&
      !isNaN(created) &&
      created >= maxAgeCutoff;

    // --- SALE ---
    // Mirrors Liquid's product-level `compare_at_price_max > price_min`. The
    // variant-level `compare_at_price` vs `price` comparison the old card used
    // can disagree with the PLP when only some variants are discounted.
    var compareMax = toNumber(hit.variants_compare_at_price_max);
    if (isNaN(compareMax)) compareMax = toNumber(hit.compare_at_price);
    var priceMin = toNumber(hit.variants_min_price);
    if (isNaN(priceMin)) priceMin = toNumber(hit.price);
    var isOnSale =
      !isNaN(compareMax) && !isNaN(priceMin) && compareMax > priceMin;

    // --- Resolve the single status badge (highest -> lowest) ---
    if (isRestocking) {
      result.status = config.labels.restocking;
      result.statusModifier = 'product-badge--restocking';
    } else if (isBackInStock) {
      result.status = config.labels.back_in_stock;
    } else if (isNew) {
      result.status = config.labels.new;
    } else if (isOnSale) {
      result.status = config.labels.sale;
    }

    // Card behaviour: the promo overrides the status badge.
    if (result.promo !== '' && result.status !== '') {
      result.status = '';
      result.statusModifier = '';
    }

    return result;
  }

  /**
   * Resolve once both the `algoliasearch` UMD bundle and the `instantsearch`
   * UMD bundle are present on `window`. The bundles are loaded via `<script
   * defer>` in `theme.liquid`, but Shopify bundles section `{% javascript %}`
   * blocks into `section.search.js` which is referenced lower in the document
   * and can execute ahead of the head-defer queue. Callers `await
   * RTLAlgolia.ready()` before mounting so the controller never races the
   * dependency load order.
   *
   * Resolves with `{ algoliasearch, instantsearch }` once both are present.
   * Polls on `requestAnimationFrame` (sub-16ms granularity) and gives up after
   * ~10s — at which point the page falls back to the no-JS shell rather than
   * spin forever.
   */
  function ready() {
    if (
      typeof window.algoliasearch !== 'undefined' &&
      typeof window.instantsearch === 'function'
    ) {
      return Promise.resolve({
        algoliasearch: window.algoliasearch,
        instantsearch: window.instantsearch,
      });
    }
    return new Promise(function (resolve, reject) {
      var started = Date.now();
      var TIMEOUT_MS = 10000;

      function tick() {
        if (
          typeof window.algoliasearch !== 'undefined' &&
          typeof window.instantsearch === 'function'
        ) {
          resolve({
            algoliasearch: window.algoliasearch,
            instantsearch: window.instantsearch,
          });
          return;
        }
        if (Date.now() - started > TIMEOUT_MS) {
          reject(
            new Error(
              'RTLAlgolia.ready: algoliasearch/instantsearch globals not ' +
                'present after ' +
                TIMEOUT_MS +
                'ms'
            )
          );
          return;
        }
        if (typeof window.requestAnimationFrame === 'function') {
          window.requestAnimationFrame(tick);
        } else {
          setTimeout(tick, 16);
        }
      }
      tick();
    });
  }

  window.RTLAlgolia = {
    createSearchClient: createSearchClient,
    getConfig: readThemeConfig,
    resetConfigCache: resetConfigCache,
    normaliseSearchParams: normaliseSearchParams,
    replaceQueryParam: replaceQueryParam,
    formatPrice: formatPrice,
    escapeHtml: escapeHtml,
    sanitiseHitUrl: sanitiseHitUrl,
    hitProductUrl: hitProductUrl,
    hitCollectionUrl: hitCollectionUrl,
    resolveBadges: resolveBadges,
    getRecommendations: getRecommendations,
    hitsToHandles: hitsToHandles,
    ready: ready,
  };
})();
